<p-toast position="top-center"></p-toast>

<main class="plan-page" [class.pricing-landing]="!managementMode() && !showRegistration()" [class.registration-page]="!managementMode() && showRegistration()">
  @if (managementMode()) {
    <section class="plan-hero compact">
      <div>
        <span class="eyebrow">Subscription management</span>
        <h1>One plan. Every module.</h1>
        <p>Choose the operating scale that matches your team. Modules are always included.</p>
      </div>
      @if (currentPlan(); as current) {
        <div class="current-plan-card">
          <span>Current plan</span>
          <strong>{{ current.plan.name }}</strong>
          <small>{{ current.status === 'TRIAL' ? (trialDaysRemaining() + ' days left in trial') : current.status }}</small>
        </div>
      }
    </section>

    @if (usage(); as planUsage) {
      <section class="usage-row">
        <article><span>Office users</span><strong>{{ planUsage.officeUsersUsed }} / {{ planUsage.officeUserLimit ?? 'Unlimited' }}</strong></article>
        <article><span>This month’s shipments</span><strong>{{ planUsage.shipmentsUsed }} / {{ planUsage.shipmentsLimit ?? 'Unlimited' }}</strong></article>
        <article><span>Billing currency</span><strong>USD</strong></article>
      </section>
    }
  } @else if (!showRegistration()) {
    <section class="plan-hero">
      <img src="/assets/logilink_logo_color.png" alt="LogiLink" class="brand-logo">
      <span class="eyebrow">Simple, transparent plans</span>
      <h1>Pricing that scales with your operation</h1>
      <p>Start free or choose a 30-day trial. Every plan includes the full CargoMate module suite — you pay for operational scale, not feature gates.</p>
    </section>
  }

  @if (!showRegistration() || managementMode()) {
    @if (catalogLoading()) {
      <section class="catalog-state" aria-live="polite">Loading plans…</section>
    } @else if (catalogError()) {
      <section class="catalog-state error" role="alert">
        <strong>{{ catalogError() }}</strong>
        <button type="button" class="retry-button" (click)="retryCatalog()">Retry</button>
      </section>
    } @else {
      <section class="plan-grid" aria-label="Subscription plans">
        @for (plan of catalog(); track plan.code) {
          <article class="plan-card" [class.popular]="plan.popular" [class.current]="currentPlan()?.planCode === plan.code">
            @if (plan.code === 'FREE') { <span class="card-badge free">Forever Free</span> }
            @if (plan.popular) { <span class="card-badge popular">Most Popular</span> }
            <h2>{{ plan.name }}</h2>
            <div class="plan-price">
              @if (plan.customPricing) {
                <strong>Custom</strong>
              } @else {
                <strong>${{ plan.monthlyPriceUsd }}</strong>
                @if (plan.listPriceUsd) { <del>${{ plan.listPriceUsd }}</del> }
                <span>/month</span>
              }
            </div>
            @if (plan.listPriceUsd) { <span class="offer">Limited-time offer</span> }
            <p class="plan-description">{{ plan.description }}</p>
            <ul>
              <li>{{ displayLimit(plan.officeUserLimit, 'office users') }}</li>
              <li>{{ displayLimit(plan.driverLimit, 'drivers') }}</li>
              <li>{{ displayLimit(plan.shipmentsLimit, 'shipments/month') }}</li>
              @for (feature of plan.features; track feature) { <li>{{ feature }}</li> }
            </ul>
            <button type="button" class="plan-action" [class.primary]="plan.popular || plan.code === 'FREE'"
              [disabled]="isPlanDisabled(plan)" (click)="selectPlan(plan)">
              {{ planButtonLabel(plan) }}
            </button>
          </article>
        }
      </section>
      @if (!managementMode()) { <p class="plan-footnote">No credit card is required. Paid trials include every module.</p> }
    }
  }

  @if (showRegistration() && !managementMode()) {
    <section id="company-registration" class="registration-shell">
      <div class="registration-layout">
        <div class="registration-form-column">
          <div class="registration-heading">
        <button type="button" class="text-button" (click)="cancelRegistration()">← Back to plans</button>
        <span class="eyebrow">{{ selectedPlan()?.code === 'FREE' ? 'Free workspace' : '30-day free trial' }}</span>
        <h1>Set up {{ selectedPlan()?.name }}</h1>
        <p>There is no payment step today. Your company gets the complete module suite immediately.</p>
          </div>

          <form [formGroup]="companyForm" class="registration-card">
        <h2>Company details</h2>
        <div class="form-grid">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="company-name" pInputText type="text" formControlName="name" autocomplete="organization">
              <label for="company-name">Company name</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
            <p-iftalabel>
              <p-select inputId="business-type" formControlName="businessType" [options]="businessTypeOptions" optionLabel="label" optionValue="value"></p-select>
              <label for="business-type">Business type</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="cr-number" pInputText type="text" formControlName="crNumber" autocomplete="off">
              <label for="cr-number">CR number</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-receipt"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="tax-number" pInputText type="text" formControlName="taxRegNumber" autocomplete="off">
              <label for="tax-number">VAT / tax number</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-envelope"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="company-email" pInputText type="email" formControlName="emailAddress" autocomplete="email">
              <label for="company-email">Company email</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="company-phone" pInputText type="tel" formControlName="phoneNumber" autocomplete="tel">
              <label for="company-phone">Company phone</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup class="span-2">
            <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="registered-address" pInputText type="text" formControlName="address" autocomplete="street-address">
              <label for="registered-address">Registered address</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-globe"></i></p-inputGroupAddon>
            <p-iftalabel>
              <p-select inputId="company-country" formControlName="countryId" [options]="countryOptions()" optionLabel="label" optionValue="value" [filter]="true"></p-select>
              <label for="company-country">Country</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="company-website" pInputText type="url" formControlName="websiteUrl" autocomplete="url">
              <label for="company-website">Website</label>
            </p-iftalabel>
          </p-inputGroup>
        </div>
      </form>

      <form [formGroup]="adminForm" class="registration-card">
        <h2>Administrator account</h2>
        <div class="form-grid">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="admin-name" pInputText type="text" formControlName="fullName" autocomplete="name">
              <label for="admin-name">Full name</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-envelope"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="admin-email" pInputText type="email" formControlName="emailAddress" autocomplete="email">
              <label for="admin-email">Email</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="admin-phone" pInputText type="tel" formControlName="phoneNumber" autocomplete="tel">
              <label for="admin-phone">Phone</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-lock"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="admin-password" pInputText type="password" formControlName="password" autocomplete="new-password">
              <label for="admin-password">Password</label>
            </p-iftalabel>
          </p-inputGroup>
          <p-inputGroup class="span-2">
            <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="admin-confirm-password" pInputText type="password" formControlName="confirmPassword" autocomplete="new-password">
              <label for="admin-confirm-password">Confirm password</label>
            </p-iftalabel>
          </p-inputGroup>
        </div>
        @if (!passwordsMatch() && adminForm.get('confirmPassword')?.touched) { <p class="form-error">Passwords do not match.</p> }
        <button type="button" class="start-button" [disabled]="registering()" (click)="submitRegistration()">
          @if (registering()) {
            <i class="pi pi-spin pi-cog"></i> Workspace setup in progress
          } @else {
            {{ selectedPlan()?.code === 'FREE' ? 'Start Free' : 'Start 30-Day Free Trial' }}
          }
        </button>
        @if (registering()) {
          <p class="provisioning-button-status"><i class="pi pi-spin pi-cog"></i> {{ provisioningStep().title }}</p>
        }
      </form>
        </div>

        <aside class="signup-promo-panel" aria-label="LogiLink workspace benefits">
          @if (registering()) {
            <section id="workspace-provisioning" class="workspace-provisioning" role="status" aria-live="polite">
              <span class="provisioning-kicker"><i class="pi pi-spin pi-cog"></i> Workspace setup in progress</span>
              <h2>{{ provisioningStep().title }}</h2>
              <p>{{ provisioningStep().detail }}</p>

              <div class="provisioning-meter" role="progressbar" aria-label="Workspace provisioning progress" aria-valuemin="0" aria-valuemax="100" [attr.aria-valuenow]="provisioningProgress()">
                <span [style.width.%]="provisioningProgress()"></span>
              </div>
              <p class="provisioning-counter">Preparing setup {{ provisioningStepIndex() + 1 }} of {{ provisioningSteps.length }}</p>

              <ol class="provisioning-checklist">
                @for (step of provisioningStepWindow(); track step.title) {
                  <li [class.complete]="step.index < provisioningStepIndex()" [class.current]="step.index === provisioningStepIndex()">
                    <i [class]="step.index < provisioningStepIndex() ? 'pi pi-check-circle' : step.icon"></i>
                    <span><strong>{{ step.title }}</strong><small>{{ step.detail }}</small></span>
                  </li>
                }
              </ol>

              <div class="provisioning-reassurance"><i class="pi pi-heart"></i> Your workspace is being prepared just for you.</div>
            </section>
          } @else {
          <div class="promo-glow promo-glow-one" aria-hidden="true"></div>
          <div class="promo-glow promo-glow-two" aria-hidden="true"></div>
          <div class="promo-plan-chip"><i class="pi pi-sparkles"></i>{{ selectedPlan()?.name }} plan</div>
          <h2>Build the operation you want to run.</h2>
          <p>Everything your team needs to move freight with control, visibility, and momentum.</p>

          <div class="promo-visual" aria-hidden="true">
            <div class="promo-visual-topline">
              <span><i class="pi pi-send"></i> Freight in motion</span>
              <i class="pi pi-ellipsis-h"></i>
            </div>
            <div class="promo-route-line"><span class="route-stop route-stop-origin"></span><span class="route-stop route-stop-destination"></span></div>
            <div class="promo-visual-metrics">
              <span><i class="pi pi-chart-line"></i> Ready to scale</span>
              <span><i class="pi pi-check-circle"></i> All systems go</span>
            </div>
          </div>

          <ul class="promo-benefits">
            <li><i class="pi pi-box"></i><span><strong>Every CargoMate module</strong> included from day one.</span></li>
            <li><i class="pi pi-credit-card"></i><span><strong>No card required</strong> today.</span></li>
            <li><i class="pi pi-arrow-up-right"></i><span><strong>Scale users and shipments</strong> when you are ready.</span></li>
          </ul>

          <div class="promo-trial-note">
            <i class="pi pi-shield"></i>
            <span>{{ selectedPlan()?.code === 'FREE' ? 'Start free today' : '30-day trial included' }}</span>
          </div>
          }
        </aside>
      </div>
    </section>
  }
</main>
